import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, Grid, Icon, styled } from '@mui/material';
import {
  Button,
  Col,
  DatePicker,
  Row,
  Select,
  Table,
  Tooltip,
  Popover,
  List,
  Typography,
  Card as AntCard
} from 'antd';
import { SearchOutlined, WarningOutlined, DownloadOutlined } from '@ant-design/icons';
import Chart from '@components/charts/Chart';
import { COLORS } from '@components/charts/chart.constant';
import dayjs from 'dayjs';
import moment from 'moment';
import ContainerBody from '@components/ContainerBody';
import Breadcrumb from '@components/Breadcrumb';
import { usePermission } from '@contexts/Auth/UsePermission';
import Page403 from '@page/Common/403/403';
import { showError } from '@components/notification';
import {
  apiGetAllDMA,
  apiGetDMAMonitoring,
  apiGetDMAMonitoringChart,
  apiExportDMAMonitoring
} from '@services/ManageDMA/DMAServices';
import { formatCurrency } from '@utils/utils';
import './style.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

// ─── Styled summary card (matches statistics page style) ───────────────────
const StyledCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '24px !important',
  background: theme.palette.background.paper,
  [theme.breakpoints.down('sm')]: { padding: '16px !important' }
}));

const ContentBox = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  '& .icon': { opacity: 0.6, fontSize: '44px', color: '#0284c7' }
}));

const CardLabel = styled('p')(() => ({
  margin: 0,
  marginTop: '4px',
  fontSize: '13px',
  fontWeight: 500,
  color: '#0284c7'
}));

const CardValue = styled('p')(() => ({
  margin: 0,
  marginTop: '6px',
  fontSize: '20px',
  fontWeight: 600,
  color: '#0284c7'
}));

// ─── Loss-level color helper ────────────────────────────────────────────────
function lossCellStyle(lossPct) {
  if (lossPct === null || lossPct === undefined) return {};
  if (lossPct < 10) return { background: '#C1E384', color: '#254000', fontWeight: 700 };
  if (lossPct < 20) return { background: '#F5E727', color: '#614700', fontWeight: 700 };
  return { background: '#e11d48', color: '#fff', fontWeight: 700 };
}

function lossRowClass(lossPct) {
  if (lossPct === null || lossPct === undefined) return '';
  if (lossPct < 10) return 'dma-row-green';
  if (lossPct < 20) return 'dma-row-yellow';
  return 'dma-row-red';
}

// ────────────────────────────────────────────────────────────────────────────

const DMAMonitoringPage = () => {
  const { dmaMonitoring } = usePermission();

  // ─── State ────────────────────────────────────────────────────────────────
  const [dmaOptions, setDmaOptions] = useState([]);
  const [selectedDma, setSelectedDma] = useState(undefined);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'day'),
    dayjs().subtract(1, 'day')
  ]);
  const [dateStrings, setDateStrings] = useState([
    moment().subtract(30, 'day').format('YYYY-MM-DD'),
    moment().subtract(1, 'day').format('YYYY-MM-DD')
  ]);

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({
    total_master: 0,
    total_customer: 0,
    loss: 0,
    loss_pct: 0
  });
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [flagSearch, setFlagSearch] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // ─── Load DMA options ────────────────────────────────────────────────────
  useEffect(() => {
    apiGetAllDMA({ page: 1, size: 1000 })
      .then((res) => {
        const rows = res?.data?.data || [];
        const options = rows.map((d) => ({ value: d.name, label: `${d.code} - ${d.name}` }));
        setDmaOptions(options);
        if (options.length > 0) {
          setSelectedDma(options[0].value);
          setFlagSearch((f) => !f);
        }
      })
      .catch(() => {});
  }, []);

  // ─── Fetch monitoring data ────────────────────────────────────────────────
  const fetchData = async () => {
    try {
      setLoading(true);
      const [tableRes, chartRes] = await Promise.all([
        apiGetDMAMonitoring({
          dmaName: selectedDma || undefined,
          fromDate: dateStrings[0],
          toDate: dateStrings[1],
          page,
          size: pageSize
        }),
        apiGetDMAMonitoringChart({
          dmaName: selectedDma || undefined,
          fromDate: dateStrings[0],
          toDate: dateStrings[1]
        })
      ]);
      const rows = (tableRes?.data?.data || []).map((item, idx) => ({
        ...item,
        stt: (page - 1) * pageSize + idx + 1,
        key: `${item.dma_id}_${item.date}_${idx}`
      }));
      setData(rows);
      setTotal(tableRes?.data?.meta?.total || 0);
      setSummary(
        tableRes?.data?.meta?.summary || {
          total_master: 0,
          total_customer: 0,
          loss: 0,
          loss_pct: 0
        }
      );
      const allRows = (chartRes?.data?.data || [])
        .slice()
        .sort((a, b) => (a._date_sort < b._date_sort ? -1 : a._date_sort > b._date_sort ? 1 : 0))
        .map((item, idx) => ({
          ...item,
          key: `chart_${item.dma_id}_${item.date}_${idx}`
        }));
      setChartData(allRows);
      setSelectedRow(null);
    } catch {
      showError('Có lỗi xảy ra khi tải dữ liệu!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedDma) return;
    fetchData();
    // eslint-disable-next-line
  }, [page, pageSize, flagSearch]);

  const handleSearch = () => {
    setPage(1);
    setFlagSearch((f) => !f);
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      const res = await apiExportDMAMonitoring({
        dmaName: selectedDma || undefined,
        fromDate: dateStrings[0],
        toDate: dateStrings[1]
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `thong_ke_that_thoat_dma_${moment().format('YYYYMMDD_HHmmss')}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      showError('Có lỗi xảy ra khi xuất báo cáo!');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (dates, strings) => {
    setDateRange(dates);
    setDateStrings(strings);
  };

  // ─── Summary cards ────────────────────────────────────────────────────────
  const summaryCards = [
    {
      label: 'Tổng lượng nước vào DMA (m³)',
      value: formatCurrency(summary.total_master),
      icon: 'water_drop'
    },
    {
      label: 'Tổng tiêu thụ khách hàng (m³)',
      value: formatCurrency(summary.total_customer),
      icon: 'people'
    },
    { label: 'Tổng thất thoát (m³)', value: formatCurrency(summary.loss), icon: 'warning' },
    { label: 'Tỷ lệ thất thoát (%)', value: `${summary.loss_pct ?? 0}%`, icon: 'percent' }
  ];

  // ─── Chart data ──────────────────────────────────────────────────────────
  const dailyChartData = useMemo(
    () => [
      {
        name: 'Thất thoát (m³)',
        data: chartData.map((r) => Number(r.loss) || 0),
        type: 'bar'
      }
    ],
    [chartData]
  );

  const dailyChartLabels = useMemo(() => chartData.map((r) => r.date), [chartData]);

  const hourlyChartData = useMemo(
    () => [
      {
        name: 'Thất thoát (m³)',
        data: selectedRow?.hourly_loss || Array(24).fill(0),
        type: 'bar'
      }
    ],
    [selectedRow]
  );

  const hourlyLabels = [
    '00',
    '01',
    '02',
    '03',
    '04',
    '05',
    '06',
    '07',
    '08',
    '09',
    '10',
    '11',
    '12',
    '13',
    '14',
    '15',
    '16',
    '17',
    '18',
    '19',
    '20',
    '21',
    '22',
    '23'
  ];

  // ─── Table columns ────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'STT',
      dataIndex: 'stt',
      width: 60,
      fixed: 'left'
    },
    {
      title: 'Tên DMA',
      dataIndex: 'dma_name',
      width: 160,
      fixed: 'left',
      render: (text, record) => (
        <Tooltip title={record.dma_code}>
          <span style={{ color: '#0284c7', fontWeight: 500 }}>{text}</span>
        </Tooltip>
      )
    },
    {
      title: 'Chi nhánh',
      dataIndex: 'branch',
      width: 120
    },
    {
      title: 'Tổ quản lý',
      dataIndex: 'manage_group',
      width: 120
    },
    {
      title: 'Ngày thống kê',
      dataIndex: 'date',
      width: 130,
      render: (text) => <span style={{ fontWeight: 500 }}>{text}</span>
    },
    {
      title: 'Tổng lượng nước vào DMA (m³)',
      dataIndex: 'total_master',
      width: 170,
      align: 'right',
      render: (val) => formatCurrency(val)
    },
    {
      title: 'Tổng tiêu thụ khách hàng (m³)',
      dataIndex: 'total_customer',
      width: 170,
      align: 'right',
      render: (val) => formatCurrency(val)
    },
    {
      title: 'Thất thoát (m³)',
      dataIndex: 'loss',
      width: 130,
      align: 'right',
      render: (val) => formatCurrency(val)
    },
    {
      title: 'Tỷ lệ thất thoát (%)',
      dataIndex: 'loss_pct',
      width: 180,
      align: 'center',
      render: (val, record) => {
        if (record.has_incomplete_data) {
          return (
            <span style={{ fontSize: 12, color: '#8c8c8c', fontStyle: 'italic' }}>
              Chưa đủ dữ liệu tính toán
            </span>
          );
        }
        return <span style={{ fontWeight: 700 }}>{val ?? 0}%</span>;
      },
      onCell: (record) => ({
        style: record.has_incomplete_data ? {} : lossCellStyle(record.loss_pct)
      })
    }
    // {
    //   title: 'Ghi chú',
    //   dataIndex: 'has_incomplete_data',
    //   width: 80,
    //   align: 'center',
    //   render: (_, record) => {
    //     if (!record.has_incomplete_data) return null;
    //     const content = (
    //       <List
    //         size="small"
    //         dataSource={record.incomplete_customers}
    //         renderItem={(item) => (
    //           <List.Item style={{ padding: '2px 0' }}>
    //             <span style={{ fontSize: 12 }}>
    //               <b>{item.code}</b> – {item.name} (
    //               {item.type === 'master' ? '\u0110H t\u1ed5ng' : '\u0110H KH'}): {item.reason}
    //             </span>
    //           </List.Item>
    //         )}
    //       />
    //     );
    //     return (
    //       <Popover
    //         content={content}
    //         title="Kh\u00e1ch h\u00e0ng kh\u00f4ng \u0111\u1ee7 d\u1eef li\u1ec7u"
    //         trigger="click">
    //         <Tooltip title="C\u00f3 kh\u00e1ch h\u00e0ng kh\u00f4ng \u0111\u1ee7 d\u1eef li\u1ec7u">
    //           <WarningOutlined style={{ color: '#f59e0b', fontSize: 18, cursor: 'pointer' }} />
    //         </Tooltip>
    //       </Popover>
    //     );
    //   }
    // }
  ];

  if (!dmaMonitoring?.view) {
    return (
      <ContainerBody>
        <Page403 />
      </ContainerBody>
    );
  }

  return (
    <ContainerBody>
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Breadcrumb
          homePath="/dashboard"
          routeSegments={[{ name: 'Kiểm tra rò rỉ' }, { name: 'Giám sát thất thoát theo DMA' }]}
        />
      </div>

      <div>
        <Row justify="space-between" align="middle" style={{ marginBottom: 4 }}>
          <Col>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Giám sát thất thoát theo DMA
            </Typography.Title>
          </Col>
          <Col>
            {dmaMonitoring?.export && (
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
                loading={loading}
                style={{
                  backgroundColor: '#ecfdf5',
                  borderColor: '#ecfdf5',
                  color: '#0284c7',
                  fontWeight: '500'
                }}>
                Xuất báo cáo
              </Button>
            )}
          </Col>
        </Row>
        {/* Search bar + Summary cards */}
        <AntCard style={{ marginTop: 8 }} bodyStyle={{ padding: '8px' }}>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }} align="middle">
            <Col>
              <Select
                allowClear
                showSearch
                placeholder="Chọn DMA"
                style={{ width: 260 }}
                value={selectedDma}
                onChange={setSelectedDma}
                optionFilterProp="label"
                options={dmaOptions}
              />
            </Col>
            <Col>
              <RangePicker
                value={dateRange}
                onChange={handleDateChange}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                style={{ background: '#0284c7', borderColor: '#0284c7' }}>
                Tìm kiếm
              </Button>
            </Col>
          </Row>

          {/* Summary cards */}
          <Grid container spacing={2}>
            {summaryCards.map((card, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <StyledCard elevation={3}>
                  <ContentBox>
                    <Icon className="icon">{card.icon}</Icon>
                    <Box ml="14px">
                      <CardLabel>{card.label}</CardLabel>
                      <CardValue>{card.value}</CardValue>
                    </Box>
                  </ContentBox>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        </AntCard>

        {/* Table */}
        <Table
          loading={loading}
          dataSource={data}
          columns={columns}
          rowKey="key"
          size="small"
          scroll={{ x: 'max-content' }}
          rowClassName={undefined}
          onRow={(record) => ({
            onClick: () => setSelectedRow(record),
            style: {
              cursor: 'pointer',
              backgroundColor: selectedRow?.key === record.key ? '#e6f7ff' : undefined
            }
          })}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (t) => `Tổng ${t} bản ghi`,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            }
          }}
        />

        {/* Charts */}
        {chartData && chartData.length > 0 && (
          <Row gutter={16} style={{ marginTop: 8 }}>
            {/* Daily loss chart */}
            <Col xs={24} md={12}>
              <AntCard style={{ marginBottom: 16 }}>
                <Typography.Title level={5} style={{ marginBottom: 16 }}>
                  Biểu đồ thất thoát theo ngày (m³)
                </Typography.Title>
                <Chart
                  options={{
                    colors: COLORS,
                    chart: {
                      zoom: { enabled: false },
                      toolbar: { show: true },
                      events: {
                        dataPointSelection: (event, chartContext, config) => {
                          const idx = config.dataPointIndex;
                          if (chartData && chartData[idx]) setSelectedRow(chartData[idx]);
                        }
                      }
                    },
                    plotOptions: {
                      bar: { horizontal: false, columnWidth: '18px' }
                    },
                    fill: { opacity: 1 },
                    dataLabels: { enabled: false },
                    stroke: { show: true, width: 2, colors: ['transparent'] },
                    xaxis: { categories: dailyChartLabels },
                    yaxis: { show: true, title: { text: 'Thất thoát (m³)' } },
                    grid: { show: true },
                    tooltip: {
                      y: { formatter: (val) => `${val} m³` }
                    },
                    legend: { horizontalAlign: 'left' }
                  }}
                  type="bar"
                  series={dailyChartData}
                  height={350}
                />
              </AntCard>
            </Col>

            {/* Hourly loss chart */}
            <Col xs={24} md={12}>
              <AntCard style={{ marginBottom: 16 }}>
                <Typography.Title level={5} style={{ marginBottom: 4 }}>
                  Biểu đồ thất thoát theo giờ (m³)
                </Typography.Title>
                {selectedRow ? (
                  <Typography.Text style={{ fontSize: 13, color: '#666' }}>
                    Ngày: <b>{selectedRow.date}</b> — DMA: <b>{selectedRow.dma_name}</b>
                  </Typography.Text>
                ) : (
                  <Typography.Text style={{ fontSize: 13, color: '#aaa', fontStyle: 'italic' }}>
                    Chọn một ngày trên bảng hoặc biểu đồ bên trái để xem chi tiết theo giờ
                  </Typography.Text>
                )}
                <Chart
                  options={{
                    colors: ['#F5A623'],
                    chart: {
                      zoom: { enabled: false },
                      toolbar: { show: true }
                    },
                    plotOptions: {
                      bar: { horizontal: false, columnWidth: '18px' }
                    },
                    fill: { opacity: 1 },
                    dataLabels: { enabled: false },
                    stroke: { show: true, width: 2, colors: ['transparent'] },
                    labels: hourlyLabels,
                    yaxis: { show: true, title: { text: 'Thất thoát (m³)' } },
                    grid: { show: true },
                    tooltip: {
                      y: { formatter: (val) => `${val} m³` }
                    },
                    legend: { horizontalAlign: 'left' }
                  }}
                  type="bar"
                  series={hourlyChartData}
                  height={350}
                />
              </AntCard>
            </Col>
          </Row>
        )}
      </div>
    </ContainerBody>
  );
};

export default DMAMonitoringPage;
